import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEcomApiV1CheckoutContentProvider from './ecom-api-v1-checkout-content-provider-checkout-content.http.js';

export interface CheckoutContentProvider {
  /** @format GUID */
  _id?: string | null;
}

export interface GetCheckoutContentRequest {
  /**
   * ID of the app that provides the content.
   * @format GUID
   */
  appId: string;
  /**
   * ID of the specific component content.
   * @format GUID
   */
  componentId: string;
  /**
   * When a language is specified, it will be retrieved the content in that language.
   * @minLength 1
   * @maxLength 20
   */
  languageCode?: string | null;
}

export interface GetCheckoutContentResponse {
  /** The requested CheckoutContent. */
  checkoutContent?: CheckoutContent;
}

export interface CheckoutContent {
  /**
   * Text to display in place of the "Continue shopping" link.
   * @minLength 1
   * @maxLength 30
   * @readonly
   */
  continueShoppingLink?: string | null;
  /**
   * Text to display in place of the "Order summary" title.
   * @minLength 1
   * @maxLength 30
   * @readonly
   */
  orderSummary?: string | null;
  /**
   * Text to display in place of the "Customer details" title.
   * @minLength 1
   * @maxLength 30
   * @readonly
   */
  customerDetails?: string | null;
  /**
   * Text to display in place of the "Review and place order" title.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  reviewAndPlaceOrder?: string | null;
  /**
   * Text to display in place of the "Place order" button.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  placeOrderButton?: string | null;
  /**
   * Text to display in place of the "Place order & pay" button.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  placeOrderPayButton?: string | null;
}

/** @docsIgnore */
export type GetCheckoutContentApplicationErrors =
  | {
      code?: 'TRANSLATIONS_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'REQUIRED_APP_NOT_INSTALLED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'REQUIRED_COMPONENT_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    };

/**
 * Retrieves a CheckoutContent.
 * @public
 * @documentationMaturity preview
 * @requiredField identifiers
 * @requiredField identifiers.appId
 * @requiredField identifiers.componentId
 * @permissionId ECOM.CHECKOUT_CONTENT_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.checkout_content_spi_host.v1.CheckoutContentSPIHostService.GetCheckoutContent
 */
export async function getCheckoutContent(
  identifiers: NonNullablePaths<
    GetCheckoutContentIdentifiers,
    `appId` | `componentId`,
    2
  >,
  options?: GetCheckoutContentOptions
): Promise<
  GetCheckoutContentResponse & {
    __applicationErrorsType?: GetCheckoutContentApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    appId: identifiers?.appId,
    componentId: identifiers?.componentId,
    languageCode: options?.languageCode,
  });

  const reqOpts =
    ambassadorWixEcomApiV1CheckoutContentProvider.getCheckoutContent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          appId: '$[0].appId',
          componentId: '$[0].componentId',
          languageCode: '$[1].languageCode',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetCheckoutContentIdentifiers {
  /**
   * ID of the app that provides the content.
   * @format GUID
   */
  appId: string;
  /**
   * ID of the specific component content.
   * @format GUID
   */
  componentId: string;
}

export interface GetCheckoutContentOptions {
  /**
   * When a language is specified, it will be retrieved the content in that language.
   * @minLength 1
   * @maxLength 20
   */
  languageCode?: string | null;
}
